export type RemoteMediaKind = "image" | "video";

const IMAGE_EXT = new Set([
	"jpg",
	"jpeg",
	"png",
	"webp",
	"gif",
	"bmp",
	"heic",
	"heif",
	"avif",
	"svg",
]);

const VIDEO_EXT = new Set(["mp4", "webm", "mov", "m4v"]);

function basenameOf(pathOrUrl: string): string {
	const trimmed = pathOrUrl.trim();
	if (!trimmed) return "";

	let path = trimmed;
	try {
		if (/^https?:\/\//i.test(trimmed)) {
			path = new URL(trimmed).pathname;
		}
	} catch {
		path = trimmed;
	}

	const withoutQuery = path.split("?")[0]?.split("#")[0] ?? path;
	return decodeURIComponent(withoutQuery.split("/").pop() ?? "").trim();
}

function extensionOf(fileName: string): string {
	const dot = fileName.lastIndexOf(".");
	if (dot < 0 || dot === fileName.length - 1) return "";
	return fileName.slice(dot + 1).toLowerCase();
}

/**
 * Image vs video from the signed-upload key (filename extension on the path).
 * Legacy keys without an extension return null.
 */
export function mediaKindFromPath(pathOrUrl: string): RemoteMediaKind | null {
	const ext = extensionOf(basenameOf(pathOrUrl));
	if (!ext) return null;
	if (VIDEO_EXT.has(ext)) return "video";
	if (IMAGE_EXT.has(ext)) return "image";
	return null;
}

/** MIME first, then filename extension (Windows often sends an empty type). */
export function mediaKindFromFile(file: File): RemoteMediaKind | null {
	const mime = file.type.trim().toLowerCase().split(";")[0] ?? "";
	if (mime.startsWith("video/")) return "video";
	if (mime.startsWith("image/")) return "image";
	return mediaKindFromPath(file.name);
}

/** First stored key that is not a video. Legacy keys without an extension count as images. */
export function firstNonVideoPath(
	paths: Array<string | null | undefined>,
): string | undefined {
	for (const path of paths) {
		const trimmed = path?.trim();
		if (trimmed && mediaKindFromPath(trimmed) !== "video") return trimmed;
	}
	return undefined;
}
